/**
 * Feb-2026 (User-Wunsch) — Zahlungseingänge / Boni verfolgen & erfassen.
 *
 * Workflow-Schritt zwischen „Finanzen & Checkliste" und „Transport beauftragen".
 * Zwei Bereiche:
 *  1) Zahlungseingänge — gleicht eingegangene Zahlungen gegen den Endpreis ab
 *     (offener Restbetrag → Auto-Bezahlt bei 0 €).
 *  2) Boni — händlerbezogener Katalog erwarteter Boni-Arten (Dropdown, selbst
 *     anlegbar). Pro Bonus: Status „ausstehend"/„eingegangen"; bei Eingang
 *     Datum + Belegnummer.
 */
import { useState, useEffect } from "react";
import { Wallet, Plus, Trash2, Loader2, Gift, CreditCard, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import FrequentSelect from "../FrequentSelect";
import api from "../../../lib/api";

const TYPES = [
    { value: "anzahlung", label: "Anzahlung" },
    { value: "teilzahlung", label: "Teilzahlung" },
    { value: "restzahlung", label: "Restzahlung" },
    { value: "sonstiges", label: "Sonstiges" },
];
const METHODS = [
    { value: "ueberweisung", label: "Überweisung" },
    { value: "bar", label: "Bar" },
    { value: "ec_karte", label: "EC/Kartenzahlung" },
    { value: "finanzierung", label: "Finanzierung" },
    { value: "leasing", label: "Leasing" },
    { value: "sonstiges", label: "Sonstiges" },
];

const fmtEur = (n) => `${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const todayISO = () => new Date().toISOString().slice(0, 10);

// Jun-2026 (User-Wunsch) — Boni-Zeile aus einer Katalog-Boni-Art bauen; respektiert
// den Default-Typ (€ oder %). Bei % bezieht sich der Wert auf den Fahrzeug-EK netto.
const rowFromType = (t) => {
    const isPct = t?.default_amount_type === "percent";
    return {
        bonus_type: t?.name || "",
        unit: isPct ? "pct" : "eur",
        amount: isPct ? "" : (t?.default_amount ? String(t.default_amount) : ""),
        pct: isPct ? (t?.default_amount ? String(t.default_amount) : "") : "",
        status: "ausstehend", date: "", belegnummer: "", note: "",
    };
};

export function PaymentReceiptsBody({ listingId, existing, condition, onClose, onSaved, embedded = false }) {
    // Feb-2026 (User-Wunsch) — Boni sind nur bei Neufahrzeug, Tageszulassung und
    // Vorführfahrzeug möglich; bei Gebrauchtfahrzeug wird der Boni-Bereich ausgeblendet.
    const boniAllowed = ["Neufahrzeug", "Tageszulassung", "Vorführfahrzeug"].includes(condition);
    const [finalPrice, setFinalPrice] = useState(Number(existing?.final_price || 0));
    const splitInit = (existing?.entries || []);
    const [receipts, setReceipts] = useState(splitInit.filter((e) => e.type !== "bonus").map((e) => ({ ...e })));
    const [boni, setBoni] = useState(splitInit.filter((e) => e.type === "bonus").map((e) => ({ ...e })));
    const [bonusTypes, setBonusTypes] = useState([]);
    const [pctBase, setPctBase] = useState(0); // EK netto (ohne Transport/Logistik) — Basis für %-Boni
    const [saving, setSaving] = useState(false);
    // Inline-Anlage einer neuen Boni-Art (händlerbezogen).
    const [showNewType, setShowNewType] = useState(false);
    const [newTypeName, setNewTypeName] = useState("");
    const [newTypeAmount, setNewTypeAmount] = useState("");
    const [newTypeUnit, setNewTypeUnit] = useState("eur");
    const [creatingType, setCreatingType] = useState(false);

    // Beim Öffnen: frische Receipts/Boni laden (Endpreis aus dispo_payment) +
    // den händlerbezogenen Boni-Katalog. Jun-2026 (User-Wunsch): sind noch keine
    // Boni erfasst, werden ALLE Boni-Arten des Katalogs vorausgewählt (Standard
    // angehakt) — nicht zutreffende kann der Händler einzeln löschen.
    useEffect(() => {
        let active = true;
        const hadSeedBoni = splitInit.filter((e) => e.type === "bonus").length > 0;
        Promise.all([
            api.get(`/listings/${listingId}/dispo/payment-receipts`).then((r) => r.data).catch(() => null),
            api.get(`/dealer/bonus-types`, { params: { listing_id: listingId } }).then((r) => r.data?.items || []).catch(() => []),
        ]).then(([rec, types]) => {
            if (!active) return;
            setBonusTypes(types);
            if (rec && typeof rec.bonus_pct_base === "number") setPctBase(rec.bonus_pct_base);
            let serverBoni = [];
            if (rec) {
                if (typeof rec.final_price === "number") setFinalPrice(rec.final_price);
                if (Array.isArray(rec.entries) && rec.entries.length) {
                    setReceipts(rec.entries.filter((e) => e.type !== "bonus").map((e) => ({ ...e })));
                    serverBoni = rec.entries.filter((e) => e.type === "bonus").map((e) => ({ ...e }));
                    setBoni(serverBoni);
                }
            }
            if (boniAllowed && !hadSeedBoni && serverBoni.length === 0 && types.length) {
                setBoni(types.map((t) => rowFromType(t)));
            }
        });
        return () => { active = false; };
    }, [listingId]);

    // --- Zahlungseingänge ----------------------------------------------------
    const addRow = () => setReceipts((p) => [...p, { date: todayISO(), amount: "", type: "teilzahlung", method: "ueberweisung", note: "" }]);
    const removeRow = (i) => setReceipts((p) => p.filter((_, idx) => idx !== i));
    const setField = (i, k, v) => setReceipts((p) => p.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));

    // Jul-2026 (User-Wunsch) — Zahlungsbeleg-Upload (z. B. Überweisungsbeleg).
    const [uploadingBeleg, setUploadingBeleg] = useState(null);
    const uploadBeleg = async (i, file) => {
        if (!file) return;
        setUploadingBeleg(i);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/listings/${listingId}/dispo/payment-receipts/upload-beleg`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setReceipts((p) => p.map((e, idx) => (idx === i
                ? { ...e, beleg_file_url: r.data.file_url, beleg_file_name: r.data.file_name } : e)));
            toast.success("Zahlungsbeleg hochgeladen");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
        } finally { setUploadingBeleg(null); }
    };

    // Jul-2026 (User-Wunsch) — bei Zahlungsart „EC/Kartenzahlung": Betrag direkt
    // ans gekoppelte SumUp-Terminal senden (kein manuelles Eintippen am Gerät).
    // Nach dem Senden wird der Status gepollt; bei Erfolg verbucht das Backend
    // die Zahlung AUTOMATISCH als Zahlungseingang (EC-Karte) in der Dispo.
    const [sendingTerminal, setSendingTerminal] = useState(null);
    const [watchingPayment, setWatchingPayment] = useState(false);
    // Jul-2026 (User-Wunsch): Terminal-Button nur zeigen, wenn unter Buchhaltung
    // ein SumUp-Terminal per Pairing-Code gekoppelt wurde.
    const [terminalReady, setTerminalReady] = useState(false);
    useEffect(() => {
        api.get("/buchhaltung/ec/sumup/readers")
            .then((r) => setTerminalReady((r.data?.items || []).length > 0))
            .catch(() => setTerminalReady(false));
    }, []);
    const reloadReceipts = async () => {
        try {
            const r = await api.get(`/listings/${listingId}/dispo/payment-receipts`);
            if (Array.isArray(r.data?.entries)) {
                setReceipts(r.data.entries.filter((e) => e.type !== "bonus").map((e) => ({ ...e })));
                setBoni(r.data.entries.filter((e) => e.type === "bonus").map((e) => ({ ...e })));
            }
        } catch { /* noop */ }
    };
    const watchTerminalPayment = async (paymentId) => {
        setWatchingPayment(true);
        try {
            for (let n = 0; n < 24; n += 1) {
                await new Promise((res) => setTimeout(res, 4000));
                let st;
                try {
                    const r = await api.post(`/buchhaltung/ec/sumup/terminal-payments/${paymentId}/check`);
                    st = r.data?.status;
                } catch { break; }
                if (st === "booked") {
                    toast.success("Zahlung erfolgreich — automatisch als Zahlungseingang verbucht");
                    await reloadReceipts();
                    onSaved?.();
                    return;
                }
                if (st === "successful") {
                    toast.success("Zahlung am Terminal erfolgreich");
                    return;
                }
                if (st === "failed") {
                    toast.error("Terminal-Zahlung fehlgeschlagen oder abgebrochen");
                    return;
                }
            }
            toast.info("Terminal-Zahlung noch offen — Abgleich erfolgt beim nächsten SumUp-Sync");
        } finally { setWatchingPayment(false); }
    };
    const sendToTerminal = async (i) => {
        const e = receipts[i];
        const val = parseFloat(e.amount) || 0;
        if (val <= 0) { toast.error("Bitte zuerst einen Betrag eingeben"); return; }
        setSendingTerminal(i);
        try {
            const label = TYPES.find((t) => t.value === e.type)?.label || e.type || "Zahlung";
            const r = await api.post("/buchhaltung/ec/sumup/terminal-checkout", {
                amount: val,
                description: `Dispo ${label}${e.note ? ` · ${e.note}` : ""}`.slice(0, 140),
                listing_id: listingId,
                receipt_type: e.type,
            });
            toast.success(`${fmtEur(val)} an das SumUp-Terminal gesendet — Karte am Gerät vorhalten`);
            const pid = r.data?.payment?.id;
            if (pid) watchTerminalPayment(pid);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Zahlung konnte nicht ans Terminal gesendet werden");
        } finally { setSendingTerminal(null); }
    };

    // --- Boni ----------------------------------------------------------------
    // Jun-2026 (User-Wunsch) — Berechnung wahlweise in € oder %; bei % bezieht sich
    // der Wert auf den Fahrzeug-Einkaufspreis NETTO (ohne Transport-/Logistikkosten).
    const boniBase = Number(pctBase) || 0;
    const effAmt = (e) => {
        if (e.unit === "pct") {
            const pct = parseFloat(e.pct) || 0;
            return Math.round(boniBase * pct) / 100;
        }
        return parseFloat(e.amount) || 0;
    };
    const addBoni = () => {
        const first = bonusTypes[0];
        setBoni((p) => [...p, first ? rowFromType(first) : {
            bonus_type: "", unit: "eur", amount: "", pct: "",
            status: "ausstehend", date: "", belegnummer: "", note: "",
        }]);
    };
    const removeBoni = (i) => setBoni((p) => p.filter((_, idx) => idx !== i));
    const setBoniField = (i, k, v) => setBoni((p) => p.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, [k]: v };
        // Boni-Art gewählt → Einheit + Wert aus dem Katalog-Default übernehmen.
        if (k === "bonus_type") {
            const t = bonusTypes.find((bt) => bt.name === v);
            if (t) {
                const isPct = t.default_amount_type === "percent";
                next.unit = isPct ? "pct" : "eur";
                if (isPct) { next.pct = t.default_amount ? String(t.default_amount) : (e.pct || ""); next.amount = ""; }
                else { next.amount = t.default_amount ? String(t.default_amount) : (e.amount || ""); next.pct = ""; }
            }
        }
        // Status zurück auf „ausstehend" → Eingangsdaten leeren.
        if (k === "status" && v === "ausstehend") { next.date = ""; next.belegnummer = ""; }
        if (k === "status" && v === "eingegangen" && !e.date) { next.date = todayISO(); }
        return next;
    }));

    const createBonusType = async () => {
        const name = newTypeName.trim();
        if (!name) { toast.error("Bitte einen Namen für die Boni-Art angeben"); return; }
        setCreatingType(true);
        try {
            const r = await api.post(`/dealer/bonus-types`, { name, default_amount: Number(newTypeAmount) || 0, default_amount_type: newTypeUnit });
            const bt = r.data?.bonus_type;
            if (bt) {
                setBonusTypes((p) => p.some((x) => x.id === bt.id) ? p : [...p, bt].sort((a, b) => a.name.localeCompare(b.name)));
                toast.success(r.data?.duplicate ? "Boni-Art existiert bereits" : "Boni-Art angelegt");
            }
            setNewTypeName(""); setNewTypeAmount(""); setNewTypeUnit("eur"); setShowNewType(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
        } finally { setCreatingType(false); }
    };

    const totalReceived = receipts.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const totalBoni = boni.reduce((s, e) => s + effAmt(e), 0);
    const boniOpen = boni.filter((e) => e.status !== "eingegangen").reduce((s, e) => s + effAmt(e), 0);
    const boniReceived = boni.filter((e) => e.status === "eingegangen").reduce((s, e) => s + effAmt(e), 0);
    const balance = finalPrice ? Math.round((finalPrice - totalReceived) * 100) / 100 : null;
    const settled = finalPrice > 0 && balance !== null && balance <= 0.01;

    const save = async () => {
        setSaving(true);
        try {
            const payEntries = receipts.filter((e) => (parseFloat(e.amount) || 0) > 0);
            const boniEntries = boni
                .filter((e) => effAmt(e) > 0)
                .map((e) => ({
                    type: "bonus",
                    amount: effAmt(e),
                    unit: e.unit || "eur",
                    pct: e.unit === "pct" ? (parseFloat(e.pct) || 0) : null,
                    pct_base: e.unit === "pct" ? boniBase : null,
                    bonus_type: e.bonus_type || "",
                    status: e.status || "ausstehend",
                    date: e.status === "eingegangen" ? (e.date || todayISO()) : "",
                    belegnummer: e.status === "eingegangen" ? (e.belegnummer || "") : "",
                    note: e.note || "",
                }));
            const r = await api.post(`/listings/${listingId}/dispo/payment-receipts`, {
                entries: [...payEntries, ...boniEntries],
            });
            toast.success("Zahlungseingänge & Boni gespeichert");
            onSaved?.(r.data?.receipts);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
                {/* Abgleich-Leiste */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Endpreis</div>
                        <div className="font-mono-num font-bold text-[#111827]" data-testid="receipts-final-price">{finalPrice ? fmtEur(finalPrice) : "—"}</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Eingegangen</div>
                        <div className="font-mono-num font-bold text-emerald-700" data-testid="receipts-total-received">{fmtEur(totalReceived)}</div>
                    </div>
                    <div className={`rounded-sm px-3 py-2 border ${settled ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Offener Restbetrag</div>
                        <div className={`font-mono-num font-bold ${settled ? "text-emerald-700" : "text-amber-700"}`} data-testid="receipts-balance">
                            {balance === null ? "—" : fmtEur(Math.max(balance, 0))}
                        </div>
                    </div>
                </div>
                {settled && (
                    <div className="flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 mb-2" data-testid="receipts-auto-paid-hint">
                        <Wallet className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>Restbetrag ausgeglichen — beim Speichern wird der Schritt <strong>„Zahlung &amp; Rabatt&quot;</strong> automatisch als bezahlt markiert{finalPrice ? " und eine vorhandene Rechnung auf bezahlt gesetzt" : ""}.</span>
                    </div>
                )}

                {/* === Zahlungseingänge === */}
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mt-1 mb-1">Zahlungseingänge</h4>
                <div className="space-y-2">
                    {receipts.length === 0 && (
                        <div className="text-sm text-gray-400 text-center py-3" data-testid="receipts-empty">Noch keine Zahlungseingänge erfasst.</div>
                    )}
                    {receipts.map((e, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end border border-gray-200 rounded-sm p-2" data-testid={`receipt-row-${i}`}>
                            <div className="col-span-3">
                                <Label className="text-[10px] uppercase text-gray-500">Datum</Label>
                                <Input type="date" value={e.date || ""} onChange={(ev) => setField(i, "date", ev.target.value)} className="h-8 rounded-sm font-mono-num" data-testid={`receipt-date-${i}`} />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] uppercase text-gray-500">Betrag €</Label>
                                <Input type="number" min="0" step="0.01" value={e.amount} onChange={(ev) => setField(i, "amount", ev.target.value)} placeholder="0,00" className="h-8 rounded-sm font-mono-num" data-testid={`receipt-amount-${i}`} />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] uppercase text-gray-500">Typ</Label>
                                <FrequentSelect
                                    storageKey="dispo.receipt_type"
                                    value={e.type}
                                    onValueChange={(v) => setField(i, "type", v)}
                                    options={TYPES}
                                    triggerClassName="h-8 rounded-sm"
                                    testId={`receipt-type-${i}`}
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] uppercase text-gray-500">Zahlungsart</Label>
                                <FrequentSelect
                                    storageKey="dispo.receipt_method"
                                    value={e.method || "ueberweisung"}
                                    onValueChange={(v) => setField(i, "method", v)}
                                    options={METHODS}
                                    triggerClassName="h-8 rounded-sm"
                                    testId={`receipt-method-${i}`}
                                />
                            </div>
                            <div className="col-span-2">
                                <Label className="text-[10px] uppercase text-gray-500">Vermerk</Label>
                                <Input value={e.note || ""} onChange={(ev) => setField(i, "note", ev.target.value)} placeholder="Referenz …" className="h-8 rounded-sm" data-testid={`receipt-note-${i}`} />
                            </div>
                            <div className="col-span-1 flex justify-end">
                                <button type="button" onClick={() => removeRow(i)} className="h-8 w-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-sm" data-testid={`receipt-remove-${i}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                            {(e.method === "ueberweisung" || e.method === "bar") && (
                                <div className="col-span-12 flex flex-wrap items-center gap-2 pt-1.5 border-t border-dashed border-gray-200" data-testid={`receipt-beleg-row-${i}`}>
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Zahlungsbeleg</span>
                                    {e.beleg_file_url ? (
                                        <a href={`${process.env.REACT_APP_BACKEND_URL}${e.beleg_file_url}`} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 hover:underline"
                                            data-testid={`receipt-beleg-link-${i}`}>
                                            <FileText className="w-3.5 h-3.5" /> {e.beleg_file_name || "Beleg ansehen"}
                                        </a>
                                    ) : (
                                        <span className="text-[11px] text-amber-700">noch kein Beleg hochgeladen</span>
                                    )}
                                    <label className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-gray-300 text-[11px] font-bold cursor-pointer hover:bg-gray-50" data-testid={`receipt-beleg-upload-${i}`}>
                                        {uploadingBeleg === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                                        {e.beleg_file_url ? "Ersetzen" : "Beleg hochladen"}
                                        <input type="file" accept="application/pdf,image/*" className="hidden"
                                            onChange={(ev) => { uploadBeleg(i, ev.target.files?.[0]); ev.target.value = ""; }} />
                                    </label>
                                </div>
                            )}
                            {terminalReady && e.method === "ec_karte" && (parseFloat(e.amount) || 0) > 0 && (
                                <div className="col-span-12 flex items-center justify-end gap-2 pt-1.5 border-t border-dashed border-gray-200">
                                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">SumUp EC-Terminal</span>
                                    {watchingPayment && (
                                        <span className="text-[10px] text-amber-700 inline-flex items-center gap-1" data-testid="sumup-watch-hint">
                                            <Loader2 className="w-3 h-3 animate-spin" /> Warte auf Zahlung am Terminal…
                                        </span>
                                    )}
                                    <Button type="button" size="sm" variant="outline"
                                        className="h-7 rounded-sm text-[11px] font-bold bg-emerald-600 hover:bg-emerald-500 text-white hover:text-white border-transparent"
                                        onClick={() => sendToTerminal(i)} disabled={sendingTerminal === i}
                                        data-testid={`receipt-sumup-send-${i}`}>
                                        {sendingTerminal === i ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CreditCard className="w-3.5 h-3.5 mr-1" />}
                                        {fmtEur(parseFloat(e.amount) || 0)} ans Terminal senden
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <Button type="button" variant="outline" className="mt-2 rounded-sm border-dashed w-full" onClick={addRow} data-testid="receipt-add-row">
                    <Plus className="w-4 h-4 mr-1" /> Zahlungseingang hinzufügen
                </Button>

                {/* === Boni === (nur Neufahrzeug / Tageszulassung / Vorführfahrzeug) */}
                {boniAllowed && (
                <div className="mt-5 pt-4 border-t border-gray-200" data-testid="boni-section">
                    <div className="flex items-center justify-between mb-1">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-amber-500" /> Boni (erwartet / eingegangen)</h4>
                        <div className="flex items-center gap-3 text-[11px]">
                            {boniOpen > 0 && <span className="text-amber-700">ausstehend <span className="font-mono-num font-bold">{fmtEur(boniOpen)}</span></span>}
                            {boniReceived > 0 && <span className="text-emerald-700">eingegangen <span className="font-mono-num font-bold">{fmtEur(boniReceived)}</span></span>}
                        </div>
                    </div>

                    {/* Inline-Anlage einer neuen Boni-Art (händlerbezogen) */}
                    {showNewType ? (
                        <div className="flex flex-wrap items-end gap-2 bg-amber-50 border border-amber-200 rounded-sm p-2 mb-2" data-testid="boni-new-type-form">
                            <div className="flex-1 min-w-[160px]">
                                <Label className="text-[10px] uppercase text-gray-500">Neue Boni-Art</Label>
                                <Input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="z. B. Hersteller-Verkaufsbonus" className="h-8 rounded-sm" data-testid="boni-new-type-name" />
                            </div>
                            <div className="w-32">
                                <Label className="text-[10px] uppercase text-gray-500">Standardbetrag</Label>
                                <div className="flex items-stretch gap-1">
                                    <Input type="number" min="0" step={newTypeUnit === "percent" ? "0.1" : "0.01"} value={newTypeAmount} onChange={(e) => setNewTypeAmount(e.target.value)} placeholder="optional" className="h-8 rounded-sm font-mono-num" data-testid="boni-new-type-amount" />
                                    <div className="flex rounded-sm overflow-hidden border border-gray-300 shrink-0">
                                        <button type="button" onClick={() => setNewTypeUnit("eur")} className={`px-1.5 text-[10px] font-bold ${newTypeUnit === "eur" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid="boni-new-type-unit-eur">€</button>
                                        <button type="button" onClick={() => setNewTypeUnit("percent")} className={`px-1.5 text-[10px] font-bold ${newTypeUnit === "percent" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid="boni-new-type-unit-pct">%</button>
                                    </div>
                                </div>
                            </div>
                            <Button type="button" size="sm" className="h-8 bg-[#111827] hover:bg-black text-white" onClick={createBonusType} disabled={creatingType} data-testid="boni-new-type-save">
                                {creatingType ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Anlegen"}
                            </Button>
                            <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => { setShowNewType(false); setNewTypeName(""); setNewTypeAmount(""); }} data-testid="boni-new-type-cancel">Abbrechen</Button>
                        </div>
                    ) : (
                        <button type="button" onClick={() => setShowNewType(true)} className="text-[11px] font-bold text-[#111827] underline mb-2" data-testid="boni-new-type-toggle">+ Neue Boni-Art anlegen</button>
                    )}

                    {bonusTypes.length === 0 && !showNewType && (
                        <div className="text-[11px] text-gray-400 mb-1" data-testid="boni-empty-catalog">Noch keine Boni-Arten angelegt — lege oben deine erste an.</div>
                    )}
                    {boni.length > 0 && (
                        <div className="text-[11px] text-gray-500 mb-1.5" data-testid="boni-default-hint">Alle Boni-Arten sind standardmäßig vorausgewählt — nicht zutreffende einfach entfernen. Berechnung je Zeile in € oder % (Basis bei %: Fahrzeug-EK netto {boniBase ? `= ${fmtEur(boniBase)}` : ""}, ohne Transport/Logistik).</div>
                    )}

                    <div className="space-y-2">
                        {boni.map((e, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 items-end border border-gray-200 rounded-sm p-2" data-testid={`boni-row-${i}`}>
                                <div className="col-span-4">
                                    <Label className="text-[10px] uppercase text-gray-500">Boni-Art</Label>
                                    <FrequentSelect
                                        storageKey="dispo.boni_type"
                                        value={e.bonus_type || ""}
                                        onValueChange={(v) => setBoniField(i, "bonus_type", v)}
                                        options={bonusTypes.map((t) => ({ value: t.name, label: t.name }))}
                                        placeholder="Boni-Art wählen…"
                                        triggerClassName="h-8 rounded-sm"
                                        testId={`boni-type-${i}`}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-[10px] uppercase text-gray-500">{e.unit === "pct" ? "Prozent" : "Betrag €"}</Label>
                                        <div className="flex rounded-sm overflow-hidden border border-gray-300" data-testid={`boni-unit-toggle-${i}`}>
                                            <button type="button" onClick={() => setBoniField(i, "unit", "eur")} className={`px-1.5 text-[10px] font-bold leading-none py-0.5 ${e.unit !== "pct" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid={`boni-unit-eur-${i}`}>€</button>
                                            <button type="button" onClick={() => setBoniField(i, "unit", "pct")} className={`px-1.5 text-[10px] font-bold leading-none py-0.5 ${e.unit === "pct" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid={`boni-unit-pct-${i}`}>%</button>
                                        </div>
                                    </div>
                                    {e.unit === "pct" ? (
                                        <>
                                            <Input type="number" min="0" step="0.1" value={e.pct} onChange={(ev) => setBoniField(i, "pct", ev.target.value)} placeholder="0" className="h-8 rounded-sm font-mono-num" data-testid={`boni-pct-${i}`} />
                                            <div className="text-[9px] text-gray-500 font-mono-num mt-0.5" data-testid={`boni-pct-eur-${i}`}>= {fmtEur(effAmt(e))}{boniBase ? "" : " (EK netto fehlt)"}</div>
                                        </>
                                    ) : (
                                        <Input type="number" min="0" step="0.01" value={e.amount} onChange={(ev) => setBoniField(i, "amount", ev.target.value)} placeholder="0,00" className="h-8 rounded-sm font-mono-num" data-testid={`boni-amount-${i}`} />
                                    )}
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-[10px] uppercase text-gray-500">Status</Label>
                                    <FrequentSelect
                                        storageKey="dispo.boni_status"
                                        value={e.status || "ausstehend"}
                                        onValueChange={(v) => setBoniField(i, "status", v)}
                                        options={[{ value: "ausstehend", label: "ausstehend" }, { value: "eingegangen", label: "eingegangen" }]}
                                        triggerClassName="h-8 rounded-sm"
                                        testId={`boni-status-${i}`}
                                    />
                                </div>
                                {e.status === "eingegangen" ? (
                                    <>
                                        <div className="col-span-2">
                                            <Label className="text-[10px] uppercase text-gray-500">Eingang am</Label>
                                            <Input type="date" value={e.date || ""} onChange={(ev) => setBoniField(i, "date", ev.target.value)} className="h-8 rounded-sm font-mono-num" data-testid={`boni-date-${i}`} />
                                        </div>
                                        <div className="col-span-1">
                                            <Label className="text-[10px] uppercase text-gray-500">Beleg-Nr.</Label>
                                            <Input value={e.belegnummer || ""} onChange={(ev) => setBoniField(i, "belegnummer", ev.target.value)} placeholder="Nr." className="h-8 rounded-sm font-mono-num" data-testid={`boni-beleg-${i}`} />
                                        </div>
                                    </>
                                ) : (
                                    <div className="col-span-3 flex items-end pb-1.5">
                                        <span className="text-[11px] text-amber-700 font-bold" data-testid={`boni-pending-hint-${i}`}>Noch nicht eingegangen</span>
                                    </div>
                                )}
                                <div className="col-span-1 flex justify-end">
                                    <button type="button" onClick={() => removeBoni(i)} className="h-8 w-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-sm" data-testid={`boni-remove-${i}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Button type="button" variant="outline" className="mt-2 rounded-sm border-dashed w-full" onClick={addBoni} data-testid="boni-add-row" disabled={bonusTypes.length === 0}>
                        <Plus className="w-4 h-4 mr-1" /> Bonus erfassen
                    </Button>
                </div>
                )}

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-4">
                    {!embedded && <Button type="button" variant="outline" className="rounded-sm" onClick={() => onClose?.()} data-testid="receipts-cancel">Abbrechen</Button>}
                    <Button type="button" className="rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] font-bold" onClick={save} disabled={saving} data-testid="receipts-save">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Speichern
                    </Button>
                </div>
        </>
    );
}

export function PaymentReceiptsModal(props) {
    return (
        <Dialog open onOpenChange={(o) => !o && props.onClose?.()}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="w-[96vw] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dispo-modal-receipts">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-emerald-600" /> Zahlungseingänge / Boni
                    </DialogTitle>
                </DialogHeader>
                <PaymentReceiptsBody {...props} />
            </DialogContent>
        </Dialog>
    );
}
